import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getFavorites, addFavorite, removeFavorite } from '../services/favoritesService';

const FavoritesContext = createContext(null);
const CACHE_PREFIX = 'fav_cache_';

function readCache(uid) {
  if (!uid) return [];
  try { return JSON.parse(localStorage.getItem(CACHE_PREFIX + uid)) || []; } catch { return []; }
}
function writeCache(uid, items) {
  if (!uid) return;
  try { localStorage.setItem(CACHE_PREFIX + uid, JSON.stringify(items)); } catch {}
}

export function useFavorites() {
  return useContext(FavoritesContext);
}

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;
  const [favorites, setFavorites] = useState(() => readCache(uid));
  const [loaded, setLoaded] = useState(() => readCache(uid).length > 0);
  const favRef = useRef(favorites);
  useEffect(() => { favRef.current = favorites; }, [favorites]);

  useEffect(() => {
    if (!uid) { setFavorites([]); setLoaded(false); return; }
    const cached = readCache(uid);
    if (cached.length > 0) { setFavorites(cached); setLoaded(true); }
    let cancelled = false;
    getFavorites(uid)
      .then(items => {
        if (cancelled) return;
        setFavorites(items);
        writeCache(uid, items);
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [uid]);

  const isFavorite = useCallback((type, id) => {
    return favorites.some(f => f.type === type && f.id === id);
  }, [favorites]);

  const toggleFavorite = useCallback(async (type, id, title, extra = {}) => {
    if (!uid) return false;
    const current = favRef.current;
    const exists = current.some(f => f.type === type && f.id === id);
    if (exists) {
      const updated = current.filter(f => !(f.type === type && f.id === id));
      setFavorites(updated);
      favRef.current = updated;
      writeCache(uid, updated);
      removeFavorite(uid, type, id).catch(() => {
        setFavorites(current);
        favRef.current = current;
        writeCache(uid, current);
      });
      return false;
    } else {
      const item = { type, id, title, ...extra };
      const updated = [...current, { ...item, addedAt: Date.now() }];
      setFavorites(updated);
      favRef.current = updated;
      writeCache(uid, updated);
      addFavorite(uid, item).catch(() => {
        setFavorites(current);
        favRef.current = current;
        writeCache(uid, current);
      });
      return true;
    }
  }, [uid]);

  return (
    <FavoritesContext.Provider value={{ favorites, loaded, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}
